import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <h1 className="text-xl font-bold">Privacy Policy</h1>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-slate max-w-none">
          <p className="text-sm text-slate-500 mb-6">Last Updated: January 11, 2026</p>

          <h2>1. Introduction</h2>
          <p>
            ProjectStatusReport.com ("we," "our," or "us") is operated by Instructing.com LLC. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your 
            information when you use our service.
          </p>

          <h2>2. Information We Collect</h2>
          
          <h3>Account Information</h3>
          <ul>
            <li>Email address</li>
            <li>Name</li>
            <li>Password (encrypted)</li>
            <li>Company name (optional)</li>
          </ul>

          <h3>Project Data</h3>
          <ul>
            <li>Project names and descriptions</li>
            <li>Status reports and metrics</li>
            <li>Milestones, risks, and blockers</li>
            <li>Team member names mentioned in reports</li>
          </ul>

          <h3>Payment Information</h3>
          <ul>
            <li>Billing address</li>
            <li>Payment method details (processed securely by Stripe)</li>
          </ul>

          <h3>Usage Data</h3>
          <ul>
            <li>Log data (IP address, browser type, pages visited)</li>
            <li>Device information</li>
            <li>Cookies and similar technologies</li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain our service</li>
            <li>Process your transactions</li>
            <li>Send service-related communications</li>
            <li>Improve our service</li>
            <li>Respond to your requests</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2>4. Data Sharing</h2>
          <p>We do not sell your personal information. We may share data with:</p>
          <ul>
            <li><strong>Service Providers:</strong> Stripe (payments), Resend (email), Emergent.sh (hosting)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger or acquisition</li>
          </ul>

          <h2>5. Data Security</h2>
          <p>We implement industry-standard security measures including:</p>
          <ul>
            <li>HTTPS encryption for all data transmission</li>
            <li>Encrypted password storage</li>
            <li>Regular security audits</li>
            <li>Access controls and authentication</li>
          </ul>

          <h2>6. Data Retention</h2>
          <ul>
            <li><strong>Active accounts:</strong> Data retained while account is active</li>
            <li><strong>Cancelled accounts:</strong> 90-day read-only access, then deletion</li>
            <li><strong>Deleted accounts:</strong> Data removed within 30 days</li>
          </ul>

          <h2>7. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Delete your account and data</li>
            <li>Export your data</li>
            <li>Opt out of marketing communications</li>
          </ul>

          <h2>8. Cookies</h2>
          <p>We use cookies to:</p>
          <ul>
            <li>Keep you logged in</li>
            <li>Remember your preferences</li>
            <li>Analyze site usage</li>
          </ul>
          <p>You can control cookies through your browser settings.</p>

          <h2>9. Children's Privacy</h2>
          <p>
            Our service is not intended for children under 13. We do not knowingly 
            collect information from children under 13.
          </p>

          <h2>10. International Users</h2>
          <p>
            Your data is processed in the United States. By using our service, 
            you consent to data transfer to the US.
          </p>

          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this policy periodically. We will notify you of significant 
            changes via email or service notification.
          </p>

          <h2>12. Contact Us</h2>
          <p>
            For privacy-related questions:<br />
            Email: <a href="mailto:cs@instructing.com" className="text-blue-600">cs@instructing.com</a><br />
            Instructing.com LLC<br />
            United States
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-auto py-6 text-center text-sm text-slate-500">
        <div className="flex justify-center gap-4">
          <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-slate-700">Terms of Service</Link>
          <span>·</span>
          <a href="mailto:cs@instructing.com" className="hover:text-slate-700">Contact</a>
        </div>
        <p className="mt-2">© 2026 Instructing.com LLC. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PrivacyPage;
